import { useLayoutEffect, useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { Alert, Button, Drawer, Input, Modal, TextArea } from "@/shared/rd";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";
import {
  capturePrivateSession,
  isPrivateSessionCurrent,
} from "@/lib/api/private-session";
import type {
  WhatsappTemplate,
  WhatsappTemplateCategory,
  WhatsappParameterFormat,
  WhatsappJson,
} from "../types/whatsapp.types";
import {
  canVisualEdit,
  exampleValues,
  object,
  parseComponents,
  validVariables,
  variables,
  withExamples,
} from "../services/template-components";
import {
  whatsappFailureKey,
  isUncertainWrite,
  useWhatsappWrite,
} from "../services/useWhatsapp";
import { TemplatePreview } from "./TemplatePreview";
import { SearchSelect } from "@/shared/rd";
import { WaField } from "./WaField";
import { WriteFailure } from "./Shared";
import { muted } from "./styles";
const copy = keys.whatsapp;
export function TemplateEditor({
  template,
  inUse,
  onClose,
  onSaved,
}: {
  template?: WhatsappTemplate;
  inUse: boolean;
  onClose: () => void;
  /** After Meta acknowledged the write, with the template's name for the toast. */
  onSaved: (name: string) => void;
}) {
  const t = useTranslations();
  const guard = useNavigationGuard();
  const setDirtySource = guard?.setDirtySource;
  const [lifetime] = useState(capturePrivateSession);
  const write = useWhatsappWrite();
  const busy = useRef(false);
  const [name, setName] = useState(template?.name ?? "");
  const [language, setLanguage] = useState(template?.language ?? "ar");
  const [category, setCategory] = useState(template?.category ?? "UTILITY");
  const [format, setFormat] = useState(
    template ? (template.parameter_format ?? "POSITIONAL") : "NAMED",
  );
  const initialRaw = JSON.stringify(
    template?.components ?? [{ type: "BODY", text: "" }],
    null,
    2,
  );
  const [raw, setRaw] = useState(initialRaw);
  const components = parseComponents(raw);
  const visualAllowed = components && canVisualEdit(components, category);
  const [advanced, setAdvanced] = useState(
    !canVisualEdit(
      template?.components ?? [{ type: "BODY", text: "" }],
      category,
    ),
  );
  const [error, setError] = useState<string>();
  /** The error is a refused or failed write (Try again), not a validation message. */
  const [failedWrite, setFailedWrite] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const disabled = write.isPending || inUse || uncertain;
  const dirty =
    raw !== initialRaw ||
    name !== (template?.name ?? "") ||
    language !== (template?.language ?? "ar") ||
    category !== (template?.category ?? "UTILITY") ||
    format !==
      (template ? (template.parameter_format ?? "POSITIONAL") : "NAMED");
  useLayoutEffect(() => {
    setDirtySource?.("details", dirty);
    return () => setDirtySource?.("details", false);
  }, [dirty, setDirtySource]);
  const close = () => {
    if (!busy.current) {
      if (guard) guard.promptDiscard(onClose);
      else onClose();
    }
  };
  function updateComponent(type: string, patch: Record<string, WhatsappJson>) {
    const current = components ?? [];
    const old = current.find((c) => c.type === type) ?? {
      type,
      ...(type === "HEADER" ? { format: "TEXT" } : {}),
    };
    let next = { ...old, ...patch };
    if ((type === "HEADER" || type === "BODY") && "text" in patch)
      next = withExamples(next, format, exampleValues(old, format));
    const rest = current.filter((c) => c.type !== type);
    if (!(type !== "BODY" && patch.text === "")) rest.push(next);
    const order = ["HEADER", "BODY", "FOOTER", "BUTTONS"];
    setRaw(
      JSON.stringify(
        rest.sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type)),
        null,
        2,
      ),
    );
  }
  const buttonComponent = components?.find((c) => c.type === "BUTTONS");
  const buttons = Array.isArray(buttonComponent?.buttons)
    ? buttonComponent.buttons.map(object)
    : [];
  function updateButtons(next: Record<string, WhatsappJson>[]) {
    if (next.length) updateComponent("BUTTONS", { buttons: next });
    else
      setRaw(
        JSON.stringify(
          components?.filter((c) => c.type !== "BUTTONS") ?? [],
          null,
          2,
        ),
      );
  }
  function validate() {
    setFailedWrite(false);
    if (!components) {
      setError(t(copy.componentsInvalid));
      return false;
    }
    if (!advanced && visualAllowed) {
      const body = components.find((c) => c.type === "BODY");
      if (!body || typeof body.text !== "string" || !body.text.trim()) {
        setError(t(keys.errors.required));
        return false;
      }
      for (const c of components.filter(
        (c) => c.type === "HEADER" || c.type === "BODY",
      )) {
        const text = String(c.text ?? "");
        if (!validVariables(text, format)) {
          setError(t(copy.invalidVariables));
          return false;
        }
        const examples = exampleValues(c, format);
        if (variables(text).some((p) => !examples[p]?.trim())) {
          setError(t(copy.examplesRequired));
          return false;
        }
      }
      if (
        buttons.some(
          (b) =>
            b.type === "URL" &&
            variables(String(b.url ?? "")).length &&
            (!Array.isArray(b.example) || !String(b.example[0] ?? "").trim()),
        )
      ) {
        setError(t(copy.examplesRequired));
        return false;
      }
    }
    setError(undefined);
    return true;
  }
  async function submit() {
    if (!components || busy.current || inUse || uncertain) return;
    busy.current = true;
    try {
      await write.mutateAsync({
        method: template ? "PATCH" : "POST",
        path: template
          ? `templates/${encodeURIComponent(template.id)}`
          : "templates",
        data: template
          ? { components, parameter_format: format as WhatsappParameterFormat }
          : {
              name,
              language,
              category: category as WhatsappTemplateCategory,
              parameter_format: format as WhatsappParameterFormat,
              components,
            },
      });
      if (!isPrivateSessionCurrent(lifetime)) return;
      setDirtySource?.("details", false);
      onSaved(name);
    } catch (e) {
      if (isPrivateSessionCurrent(lifetime)) {
        const unknown = isUncertainWrite(e);
        setUncertain(Boolean(unknown));
        setError(t(whatsappFailureKey(e, true)));
        setFailedWrite(true);
        setConfirm(false);
      }
    } finally {
      busy.current = false;
    }
  }
  return (
    <Drawer
      open
      onClose={close}
      crumb={`${t(copy.templates)} / ${t(template ? copy.editTemplate : copy.newTemplate)}`}
      badge="doc"
      title={template ? template.name : t(copy.newTemplate)}
      sub={template ? template.language : undefined}
      closeLabel={t(copy.close)}
      width={580}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
      {inUse && <Alert tone="warn">{t(copy.inUseHint)}</Alert>}
      {error && failedWrite ? (
        <WriteFailure
          title={t(template ? "whatsapp.templateEditFailed" : "whatsapp.templateCreateFailed")}
          message={error}
          uncertain={uncertain}
          retrying={write.isPending}
          onTryAgain={inUse ? undefined : () => void submit()}
          onCheckedOutcome={() => {
            setUncertain(false);
            setError(undefined);
            setFailedWrite(false);
          }}
        />
      ) : error ? (
        <Alert tone="error" title={t("whatsapp.templateFixTitle")}>
          {error}
        </Alert>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (validate()) setConfirm(true);
        }}
        style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}
      >
        <fieldset
          disabled={disabled}
          style={{ margin: 0, padding: 0, border: 0, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            <WaField
              label={t(copy.name)}
              help={!template ? t(copy.templateNameHint) : undefined}
              required
            >
              {(id) => (
              <Input
                id={id}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                pattern="[a-z0-9_]+"
                maxLength={512}
                readOnly={Boolean(template)}
                dir="ltr"
              />
              )}
            </WaField>
            <WaField label={t(copy.language)} required>
              {(id) => (
              <Input
                id={id}
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                required
                pattern="[a-z]{2,3}(_[A-Z]{2})?"
                readOnly={Boolean(template)}
                dir="ltr"
              />
              )}
            </WaField>
            <WaField label={t(copy.category)}>
              {(id) => (
              <SearchSelect
                id={id}
                label={t(copy.category)}
                value={category}
                disabled={disabled || Boolean(template)}
                onChange={(value) => {
                  if (disabled || template) return;
                  setCategory(value);
                  if (value === "AUTHENTICATION") setAdvanced(true);
                }}
                options={(
                  ["UTILITY", "MARKETING", "AUTHENTICATION"] as const
                ).map((value) => ({ value, label: t(copy[value]) }))}
                emptyLabel={t(keys.common.noResults)}
              />
              )}
            </WaField>
            <WaField label={t(copy.format)}>
              {(id) => (
              <SearchSelect
                id={id}
                label={t(copy.format)}
                value={format}
                disabled={disabled}
                onChange={(value) => {
                  if (!disabled) setFormat(value);
                }}
                options={(["NAMED", "POSITIONAL"] as const).map((value) => ({
                  value,
                  label: t(copy[value]),
                }))}
                emptyLabel={t(keys.common.noResults)}
              />
              )}
            </WaField>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <Button
              type="button"
              size="sm"
              variant={!advanced ? "primary" : "secondary"}
              aria-pressed={!advanced}
              disabled={!visualAllowed}
              onClick={() => setAdvanced(false)}
            >
              {t(copy.visual)}
            </Button>
            <Button
              type="button"
              size="sm"
              variant={advanced ? "primary" : "secondary"}
              aria-pressed={advanced}
              onClick={() => setAdvanced(true)}
            >
              {t(copy.advanced)}
            </Button>
          </div>
          {advanced || !visualAllowed ? (
            <>
              <p style={muted}>{t(copy.advancedHint)}</p>
              <WaField label={t(copy.advanced)}>
                {(id) => (
                <TextArea
                  id={id}
                  value={raw}
                  onChange={(e) => setRaw(e.target.value)}
                  rows={18}
                  spellCheck={false}
                  dir="ltr"
                />
                )}
              </WaField>
            </>
          ) : (
            <>
              <p style={muted}>{t(copy.parameterHint)}</p>
              {(["HEADER", "BODY", "FOOTER"] as const).map((type) => {
                const component = components?.find((c) => c.type === type) ?? {
                  type,
                  text: "",
                };
                const examples = exampleValues(component, format);
                return (
                  <div key={type} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <WaField
                      label={t(
                        type === "HEADER"
                          ? copy.header
                          : type === "BODY"
                            ? copy.body
                            : copy.footer,
                      )}
                      required={type === "BODY"}
                    >
                      {(id) => (
                      <TextArea
                        id={id}
                        value={String(component.text ?? "")}
                        onChange={(e) =>
                          updateComponent(type, { text: e.target.value })
                        }
                        rows={type === "BODY" ? 5 : 2}
                        maxLength={type === "BODY" ? 1024 : 60}
                        required={type === "BODY"}
                        dir="auto"
                      />
                      )}
                    </WaField>
                    {type !== "FOOTER" &&
                      variables(String(component.text ?? "")).map((p) => (
                        <WaField
                          key={p}
                          label={t(copy.example, { parameter: p })}
                        >
                          {(id) => (
                          <Input
                            id={id}
                            value={examples[p] ?? ""}
                            onChange={(e) =>
                              updateComponent(type, {
                                example:
                                  withExamples(component, format, {
                                    ...examples,
                                    [p]: e.target.value,
                                  }).example ?? {},
                              })
                            }
                            required
                            dir="auto"
                          />
                          )}
                        </WaField>
                      ))}
                  </div>
                );
              })}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{t(copy.buttons)}</p>
                {buttons.map((button, index) => (
                  <div
                    key={index}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: 12,
                      padding: 12,
                      borderRadius: "var(--rd-radius-card)",
                      boxShadow: "var(--rd-shadow-card)",
                    }}
                  >
                    <WaField label={t(copy.buttonType)}>
                      {(id) => (
                      <SearchSelect
                        id={id}
                        label={t(copy.buttonType)}
                        value={String(button.type)}
                        disabled={disabled}
                        onChange={(value) => {
                          if (disabled) return;
                          updateButtons(
                            buttons.map((b, i) =>
                              i === index
                                ? { type: value, text: b.text ?? "" }
                                : b,
                            ),
                          );
                        }}
                        options={(
                          ["QUICK_REPLY", "URL", "PHONE_NUMBER"] as const
                        ).map((value) => ({ value, label: t(copy[value]) }))}
                        emptyLabel={t(keys.common.noResults)}
                      />
                      )}
                    </WaField>
                    <WaField label={t(copy.buttonText)} required>
                      {(id) => (
                      <Input
                        id={id}
                        value={String(button.text ?? "")}
                        onChange={(e) =>
                          updateButtons(
                            buttons.map((b, i) =>
                              i === index ? { ...b, text: e.target.value } : b,
                            ),
                          )
                        }
                        maxLength={25}
                        required
                      />
                      )}
                    </WaField>
                    {button.type === "URL" && (
                      <>
                        <WaField label={t(copy.url)} required>
                          {(id) => (
                          <Input
                            id={id}
                            value={String(button.url ?? "")}
                            onChange={(e) =>
                              updateButtons(
                                buttons.map((b, i) =>
                                  i === index
                                    ? { ...b, url: e.target.value }
                                    : b,
                                ),
                              )
                            }
                            required
                            dir="ltr"
                          />
                          )}
                        </WaField>
                        {variables(String(button.url ?? "")).length > 0 && (
                          <WaField label={t(copy.urlExample)} required>
                            {(id) => (
                            <Input
                              id={id}
                              value={
                                Array.isArray(button.example)
                                  ? String(button.example[0] ?? "")
                                  : ""
                              }
                              onChange={(e) =>
                                updateButtons(
                                  buttons.map((b, i) =>
                                    i === index
                                      ? { ...b, example: [e.target.value] }
                                      : b,
                                  ),
                                )
                              }
                              required
                              type="url"
                              dir="ltr"
                            />
                            )}
                          </WaField>
                        )}
                      </>
                    )}
                    {button.type === "PHONE_NUMBER" && (
                      <WaField label={t(copy.phoneNumber)} required>
                        {(id) => (
                        <Input
                          id={id}
                          value={String(button.phone_number ?? "")}
                          onChange={(e) =>
                            updateButtons(
                              buttons.map((b, i) =>
                                i === index
                                  ? { ...b, phone_number: e.target.value }
                                  : b,
                              ),
                            )
                          }
                          required
                          type="tel"
                          dir="ltr"
                        />
                        )}
                      </WaField>
                    )}
                    <div style={{ alignSelf: "end" }}>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        icon={X}
                        muted
                        onClick={() =>
                          updateButtons(buttons.filter((_, i) => i !== index))
                        }
                      >
                        {t(copy.removeButton)}
                      </Button>
                    </div>
                  </div>
                ))}
                <div>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  icon={Plus}
                  disabled={buttons.length >= 10}
                  onClick={() =>
                    updateButtons([
                      ...buttons,
                      { type: "QUICK_REPLY", text: "" },
                    ])
                  }
                >
                  {t(copy.addButton)}
                </Button>
                </div>
              </div>
            </>
          )}
          <div>
            <Button
              type="submit"
              variant="primary"
              disabled={inUse || write.isPending || uncertain}
            >
              {t(copy.saveTemplate)}
            </Button>
          </div>
        </fieldset>
      </form>
      {components && (
        <TemplatePreview components={components} format={format} />
      )}
      </div>
      <Modal
        open={confirm}
        onClose={() => {
          if (!busy.current) setConfirm(false);
        }}
        title={t(template ? copy.confirmEdit : copy.confirmCreate)}
        actions={
          <>
            <Button
              variant="ghost"
              muted
              disabled={write.isPending}
              onClick={() => {
                if (!busy.current) setConfirm(false);
              }}
            >
              {t(copy.cancel)}
            </Button>
            <Button
              variant="primary"
              loading={write.isPending}
              loadingLabel={t("whatsapp.submitting")}
              onClick={() => void submit()}
            >
              {t(copy.saveTemplate)}
            </Button>
          </>
        }
      >
        <p style={{ margin: 0, fontSize: 14 }}>{t(copy.confirmTemplate, { name, language })}</p>
      </Modal>
    </Drawer>
  );
}
